import { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  BarChart3,
  Bot,
  BrainCircuit,
  Globe,
  Headphones,
  Mail,
  Palette,
  ShoppingBag,
  Sparkles,
  type LucideIcon,
  Link2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { CarouselArrow, CarouselDots, CarouselHint, CARD_SHADOW } from "./carousel-ui";
import { Eyebrow } from "@/components/landing/_primitives";

type FeatureDef = {
  icon: LucideIcon;
  title: { pt: string; en: string };
  description: { pt: string; en: string };
};

const features: FeatureDef[] = [
  {
    icon: Link2,
    title: { pt: "Links que retêm atenção", en: "Links that hold attention" },
    description: {
      pt: "Uma superfície elegante para concentrar tudo o que a audiência precisa fazer em seguida.",
      en: "An elegant surface that concentrates everything your audience should do next.",
    },
  },
  {
    icon: BarChart3,
    title: { pt: "Entenda seu público", en: "Know your audience" },
    description: {
      pt: "Dados claros para saber de onde vêm os cliques, o que converte e o que vale repetir.",
      en: "Clear data so you know where clicks come from, what converts, and what is worth repeating.",
    },
  },
  {
    icon: Palette,
    title: { pt: "Aparência de alta classe", en: "High-class look" },
    description: {
      pt: "Visual premium, consistente e legível sem você precisar virar designer de interface.",
      en: "A premium, consistent, legible look without needing to become a UI designer.",
    },
  },
  {
    icon: ShoppingBag,
    title: { pt: "Venda no automático", en: "Sell automatically" },
    description: {
      pt: "Sua bio vira vitrine, checkout e entrega sem a sobrecarga de uma loja inteira.",
      en: "Your bio becomes storefront, checkout, and delivery without the overhead of a full store.",
    },
  },
  {
    icon: Mail,
    title: { pt: "Capture contatos", en: "Capture contacts" },
    description: {
      pt: "Transforme curiosidade em lista própria com formulários e iscas pensadas para conversão.",
      en: "Turn curiosity into a real list with forms and lead magnets built for conversion.",
    },
  },
  {
    icon: Globe,
    title: { pt: "A sua própria URL", en: "Your own URL" },
    description: {
      pt: "Mais confiança para audiência, clientes e parceiros com um endereço que parece realmente seu.",
      en: "More trust with a URL that actually feels like yours — and keeps your brand front and center.",
    },
  },
  {
    icon: Headphones,
    title: { pt: "Vendas e suporte 24/7", en: "24/7 Sales & Support" },
    description: {
      pt: "Um atendente permanente para responder, orientar e encaminhar quem chega com intenção.",
      en: "A permanent rep that answers, guides, and routes people who arrive with intent.",
    },
  },
  {
    icon: BrainCircuit,
    title: { pt: "Visão além dos números", en: "Vision beyond numbers" },
    description: {
      pt: "A IA conecta sinais do funil e devolve leitura acionável em vez de só um painel bonito.",
      en: "AI connects funnel signals and returns actionable insight instead of just a pretty dashboard.",
    },
  },
  {
    icon: Bot,
    title: { pt: "Agente IA na bio", en: "AI agent on your page" },
    description: {
      pt: "Responda visitantes, qualifique leads e feche vendas — mesmo enquanto você dorme.",
      en: "Answer visitors, qualify leads, and close sales — even while you sleep.",
    },
  },
  {
    icon: Sparkles,
    title: { pt: "Otimização automática", en: "Automatic optimization" },
    description: {
      pt: "Copys, ordem dos blocos e testes deixam de ser chute e viram operação com critério.",
      en: "Copy, block order, and testing stop being guesswork and become a deliberate system.",
    },
  },
];

function FeatureItem({ feature, index, language }: { feature: FeatureDef; index: number; language: "pt" | "en" }) {
  const rm = useReducedMotion();
  return (
    <motion.div
      initial={rm ? false : { opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: rm ? 0 : 0.4, delay: Math.min(index * 0.05, 0.25) }}
      className="flex items-start gap-4 sm:gap-5"
    >
      <div className="shrink-0 flex h-12 w-12 items-center justify-center rounded-full bg-[#ffe6d3] sm:h-14 sm:w-14">
        <feature.icon className="h-5 w-5 text-[#c8693a] sm:h-6 sm:w-6" strokeWidth={1.5} />
      </div>
      <div>
        <h3 className="font-editorial font-semibold text-[1rem] leading-snug text-[#201814] sm:text-[1.05rem]">
          {pickByLanguage(language, feature.title)}
        </h3>
        <p className="mt-1.5 text-sm leading-6 text-[#66584d]">
          {pickByLanguage(language, feature.description)}
        </p>
      </div>
    </motion.div>
  );
}

export function FeaturesSection() {
  const { language } = useLanguage();
  const rm = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    // find the child closest to center of scroll viewport
    const center = el.scrollLeft + el.clientWidth / 2;
    let closest = 0;
    let minDist = Infinity;
    children.forEach((child, i) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const dist = Math.abs(childCenter - center);
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    if (closest !== activeIndex) setActiveIndex(closest);
  };

  const scrollToIndex = (idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const child = el.children[idx] as HTMLElement | undefined;
    if (!child) return;
    // scroll so the child center aligns with the container center
    const targetLeft = child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2;
    el.scrollTo({ left: targetLeft, behavior: "smooth" });
  };

  return (
    <section id="features" className="bg-[#fff7ef] py-14 sm:py-20 lg:py-24 overflow-hidden">
      <div className="container">
        <motion.div
          initial={rm ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: rm ? 0 : 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <Eyebrow withDot accent="15 90% 58%" className="justify-center text-[#9a7258]">
            {pickByLanguage(language, { pt: "O estúdio por trás da sua marca", en: "The studio behind your brand" })}
          </Eyebrow>
          <h2 className="mt-4 font-editorial font-semibold text-[clamp(2rem,6vw,4rem)] leading-[1.05] sm:leading-[1.02] tracking-[-0.035em] text-[#1f1713]">
            {pickByLanguage(language, {
              pt: (
                <>
                  Toda a maestria de um time de produto{" "}
                  <span className="text-gradient-creator">na sua identidade digital.</span>
                </>
              ),
              en: (
                <>
                  The craft of a full product team{" "}
                  <span className="text-gradient-creator">behind your digital identity.</span>
                </>
              ),
            })}
          </h2>
          <p className="mt-5 text-[15px] leading-7 text-[#66584d] sm:text-lg sm:leading-8">
            {pickByLanguage(language, {
              pt: "À primeira vista, sua página é apenas bonita. Por trás dela, um estúdio inteiro cuida de cada detalhe — para sua presença parecer inevitável.",
              en: "At first glance, your page is simply beautiful. Behind it, a full studio handles every detail — so your presence feels inevitable.",
            })}
          </p>
        </motion.div>
      </div>

      {/* Mobile: carousel snap-x com dots */}
      <div className="sm:hidden">
        <div className="mt-8">
          {/* Scroll track + setas (relative isolado para top-1/2 ser só da track) */}
          <div className="relative">
            {/* Edge fade masks */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-4 z-10 bg-gradient-to-r from-[#fff7ef] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-4 z-10 bg-gradient-to-l from-[#fff7ef] to-transparent" />

            <CarouselArrow
              direction="left"
              onClick={() => scrollToIndex(activeIndex - 1)}
              disabled={activeIndex === 0}
              ariaLabel={pickByLanguage(language, { pt: "Recurso anterior", en: "Previous feature" })}
            />

            <CarouselArrow
              direction="right"
              onClick={() => scrollToIndex(activeIndex + 1)}
              disabled={activeIndex === features.length - 1}
              ariaLabel={pickByLanguage(language, { pt: "Próximo recurso", en: "Next feature" })}
            />

            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-[calc(50%-9rem)] py-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {features.map((feature, index) => (
                <div
                  key={feature.title.en}
                  className={cn("flex-shrink-0 snap-center w-[78vw] max-w-[18rem] rounded-[22px] border border-[#eadbcc] bg-white/80 p-5", CARD_SHADOW)}
                >
                  <FeatureItem feature={feature} index={index} language={language} />
                </div>
              ))}
            </div>
          </div>

          <CarouselDots
            count={features.length}
            activeIndex={activeIndex}
            onDotClick={scrollToIndex}
            ariaLabelFn={(i) => pickByLanguage(language, { pt: `Recurso ${i + 1}`, en: `Feature ${i + 1}` })}
          />

          <CarouselHint text={pickByLanguage(language, { pt: "← Deslize para ver mais →", en: "← Swipe to see more →" })} />
        </div>
      </div>

      {/* Desktop: grid 2 colunas */}
      <div className="container">
        <div className="mt-16 hidden gap-x-16 gap-y-10 sm:grid sm:grid-cols-2">
          {features.map((feature, index) => (
            <FeatureItem key={feature.title.en} feature={feature} index={index} language={language} />
          ))}
        </div>
      </div>
    </section>
  );
}

